/**
 * @fileOverview This file defines the `RegionsTable` component that displays a table of regions
 * with options to create, update, or delete entries.
 * @version 1.0.0
 * @since 1.0.0
 * @author Santiago Cano Herrera
 */

//#region Imports
import { useEffect, useState } from 'react';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { faAdd } from '@fortawesome/pro-regular-svg-icons';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DeleteOutlineOutlined as DeleteIcon } from '@mui/icons-material';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';

import { GlobalVariables } from '../../../globalVar';
import customAxios from '../../../utils/customAxios';
import { formatDateDDMMYYYY } from '../../../utils/dateFormat';
import { getToast } from '../../../utils/toastConfig';
import { useTranslationHook } from '../../../hooks/UseTranslationHook';
import { CreateRegionModal } from '../../Modal/Sigma/Regions/CreateRegionModal';
import { UpdateRegionModal } from '../../Modal/Sigma/Regions/UpdateRegionModal';
import { DeleteRegionModal } from '../../Modal/Sigma/Regions/DeleteRegionModal';
import usePersistedColumnOrder from '../../../hooks/UseCustomColumnOrder';
import { useUserPermissions } from '../../../hooks/userPermissions';
//#endregion

const LOCAL_COLUMN_WIDTH = GlobalVariables.getDefaultColumnWidth();

/**
 * Renders a table of regions with functionality to create, update, and delete entries.
 * @returns {JSX.Element} The `RegionsTable` component.
 */
export const RegionsTable = () => {
  // Define the action column with add, edit and delete buttons
  const userPermissions = useUserPermissions();

  const [isFetching, setIsFetching] = useState(false); // State for tracking API request status
  const [showCreateModal, setShowCreateModal] = useState(false); // State for showing the create modal
  const [showUpdateModal, setShowUpdateModal] = useState(false); // State for showing the update modal
  const [showDeleteModal, setShowDeleteModal] = useState(false); // State for showing the delete modal
  const [regionId, setRegionId] = useState(null); // State for storing the selected region ID
  const [regions, setRegions] = useState([]); // State for storing the fetched regions

  useEffect(() => {
    fetchData(); // Fetch regions data when the component mounts
  }, []);

  // Translation strings for table headers and actions
  const {
    addString,
    editString,
    deleteString,
    createdAtString,
    createdByString,
    updatedAtString,
    updatedByString,
    notesString,
  } = useTranslationHook();

  // Define initial table columns
  const initialColumns = [
    { field: 'id', headerName: 'ID', width: LOCAL_COLUMN_WIDTH },
    { field: 'name', headerName: 'Nombre', width: LOCAL_COLUMN_WIDTH },
    {
      field: 'created_at',
      headerName: createdAtString,
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'created_by_name',
      headerName: createdByString,
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'updated_at',
      headerName: updatedAtString,
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'updated_by_name',
      headerName: updatedByString,
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'notes',
      headerName: notesString,
      width: LOCAL_COLUMN_WIDTH,
    },
    // Map through all columns and add default renderCell if not already defined
  ].map((col) =>
    col.renderCell
      ? col
      : {
          ...col,
          renderCell: (params) =>
            ['created_at', 'updated_at'].includes(col.field)
              ? formatDateDDMMYYYY(params.row[col.field])
              : params.row[col.field] || '-',
        },
  );

  const actionColumn = [
    {
      field: 'action',
      headerName: 'Detalle',
      width: 160,
      renderCell: (params) => {
        return (
          <div
            className="cellAction equips-datagrid-actions-tour"
            style={{
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Edit Button */}
            {userPermissions.includes('Update Regions') && (
              <Tooltip title={editString} arrow>
                <IconButton
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                  }}
                  onClick={() => {
                    setRegionId(params.row.id); // Set selected region ID
                    setShowUpdateModal(true); // Show the update modal
                  }}
                >
                  <ModeEditOutlineOutlinedIcon
                    className="detail-icons"
                    id="update-icon"
                  />
                </IconButton>
              </Tooltip>
            )}
            {/* Delete Button */}
            {userPermissions.includes('Delete Regions') && (
              <Tooltip title={deleteString} arrow>
                <IconButton
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                  }}
                  onClick={() => {
                    setRegionId(params.row.id); // Set selected region ID
                    setShowDeleteModal(true); // Show the delete modal
                  }}
                >
                  <DeleteIcon className="detail-icons" id="delete-icon" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  const [columns, updateColumnOrder] = usePersistedColumnOrder(
    'regionsTable',
    initialColumns,
  );

  /**
   * Fetches regions data from the API.
   */
  const fetchData = async () => {
    setIsFetching(true); // Set fetching state to true
    const endpoint = `${GlobalVariables.getURL()}region/`;
    try {
      const response = await customAxios.get(endpoint, {
        headers: {
          Authorization: 'bearer ' + localStorage.getItem('token'),
        },
      });

      if (response.status === 204) {
        setRegions([]); // Set regions to an empty array if no data is returned
        return;
      }

      setRegions(response.data.data); // Update regions state with fetched data
    } catch (error) {
      console.error('Error fetching regions:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsFetching(false); // Set fetching state to false
    }
  };

  /**
   * Handles column order changes for the table.
   * @param {Object} params - Parameters containing target and old index.
   */
  const handleColumnOrderChange = (params) => {
    const { targetIndex, oldIndex } = params;

    const updatedColumns = [...columns];
    const [movedColumn] = updatedColumns.splice(oldIndex, 1);
    updatedColumns.splice(targetIndex, 0, movedColumn);

    updateColumnOrder(updatedColumns); // Update column order in persisted state
  };

  // Check if user has any action permissions
  const hasActionPermissions =
    userPermissions.includes('Update Regions') ||
    userPermissions.includes('Delete Regions');

  return (
    <>
      {/* Header and Add Button */}
      <div className="datatable-btns-container">
        <div className="dt-btn-main-container">
          <span className="dabiWebText">Regiones</span>
          {userPermissions.includes('Create Regions') && (
            <button
              className="add-button create-equip-tour"
              onClick={() => setShowCreateModal(true)}
            >
              {addString}
              <FontAwesomeIcon icon={faAdd} />
            </button>
          )}
        </div>
      </div>

      {/* Create Modal */}
      <CreateRegionModal
        showModal={showCreateModal}
        setShowModal={setShowCreateModal}
        refreshData={fetchData}
      />

      {/* Update Modal */}
      <UpdateRegionModal
        regionId={regionId}
        showModal={showUpdateModal}
        setShowModal={setShowUpdateModal}
        refreshData={fetchData}
      />

      {/* Delete Modal */}
      <DeleteRegionModal
        regionId={regionId}
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        refreshData={fetchData}
      />

      {/* Data Grid */}
      <DataGridPro
        rows={regions}
        columns={hasActionPermissions ? columns.concat(actionColumn) : columns}
        loading={isFetching}
        onColumnOrderChange={(params) => handleColumnOrderChange(params)}
        initialState={{
          sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }],
          },
          pinnedColumns: hasActionPermissions ? { right: ['action'] } : {},
        }}
        slots={{
          noRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              No hay regiones en la base de datos
            </Stack>
          ),
          toolbar: GridToolbar, // Toolbar with default functionality
        }}
      />
    </>
  );
};
